import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader } from "./_shared";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Loader2, Download, ChevronLeft, ChevronRight, Clock, DollarSign, Hammer, Users, ShieldAlert, ClipboardCheck, Truck, Award, Receipt, Calculator, Mail } from "lucide-react";
import { toast } from "sonner";

const NZD = new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD" });
const NUM = new Intl.NumberFormat("en-NZ");
const fmtNum = (v) => NUM.format(Math.round(Number(v || 0)));
const fmtMoney = (v) => NZD.format(Number(v || 0));

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

function makeMonthList() {
  const out = [];
  const now = new Date();
  for (let i = 0; i < 18; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push({ year: d.getFullYear(), month: d.getMonth() + 1, label: `${MONTHS[d.getMonth()]} ${d.getFullYear()}` });
  }
  return out;
}

export default function Reports() {
  const { user } = useAuth();
  const months = useMemo(makeMonthList, []);
  const [sel, setSel] = useState(months[1] || months[0]); // default to LAST month (1)
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [savingToggle, setSavingToggle] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data: resp } = await api.get(`/reports/monthly`, { params: { year: sel.year, month: sel.month } });
      setData(resp);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load report");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sel.year, sel.month]);

  const downloadPdf = async (section) => {
    setDownloading(section);
    try {
      const res = await api.get(`/reports/monthly/pdf`, {
        params: { year: sel.year, month: sel.month, section },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `fieldlink-${section}-${MONTHS[sel.month - 1].toLowerCase()}-${sel.year}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("PDF download failed");
    } finally {
      setDownloading(null);
    }
  };

  const toggleEmail = async (next) => {
    setSavingToggle(true);
    try {
      await api.post(`/reports/monthly/email-toggle`, { enabled: next });
      setData((d) => d ? { ...d, reports_email_monthly: next } : d);
      toast.success(next ? "Auto-email enabled — first send on the 1st" : "Auto-email disabled");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed");
    } finally {
      setSavingToggle(false);
    }
  };

  const prev = () => {
    const idx = months.findIndex((m) => m.year === sel.year && m.month === sel.month);
    if (idx >= 0 && idx + 1 < months.length) setSel(months[idx + 1]);
  };
  const next = () => {
    const idx = months.findIndex((m) => m.year === sel.year && m.month === sel.month);
    if (idx > 0) setSel(months[idx - 1]);
  };

  const s = data?.sections || {};

  return (
    <div data-testid="reports-page" className="p-4 lg:p-10 max-w-6xl mx-auto">
      <PageHeader
        tag="/ monthly roll-up"
        title="Monthly report"
        subtitle={data?.label ? `Performance summary for ${data.org_name} · ${data.label}` : "Pick a month to see the roll-up."}
        action={
          <Button
            onClick={() => downloadPdf("full")}
            disabled={!data || downloading === "full"}
            data-testid="reports-download-full"
            className="rounded-sm bg-[#FF5F15] hover:bg-[#e55410] text-white font-bold"
          >
            {downloading === "full" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />}
            Full PDF
          </Button>
        }
      />

      {/* Month picker + auto-email toggle */}
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <div className="inline-flex items-center gap-1.5 border border-zinc-200 dark:border-zinc-800 rounded-sm overflow-hidden">
          <button onClick={prev} className="px-2 py-1.5 text-zinc-500 hover:bg-zinc-100 dark:hover:bg-[#151C27] hover:text-zinc-900 dark:hover:text-white" data-testid="reports-month-prev"><ChevronLeft className="w-4 h-4" /></button>
          <Select value={`${sel.year}-${sel.month}`} onValueChange={(v) => {
            const [y, m] = v.split("-").map(Number);
            setSel({ year: y, month: m, label: `${MONTHS[m - 1]} ${y}` });
          }}>
            <SelectTrigger data-testid="reports-month-select" className="border-0 rounded-none w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              {months.map((m) => (
                <SelectItem key={`${m.year}-${m.month}`} value={`${m.year}-${m.month}`}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <button onClick={next} className="px-2 py-1.5 text-zinc-500 hover:bg-zinc-100 dark:hover:bg-[#151C27] hover:text-zinc-900 dark:hover:text-white" data-testid="reports-month-next"><ChevronRight className="w-4 h-4" /></button>
        </div>

        <div className="ml-auto flex items-center gap-2.5 px-3 py-2 border border-zinc-200 dark:border-zinc-800 rounded-sm">
          <Mail className="w-4 h-4 text-zinc-500" />
          <div className="text-[11.5px] font-semibold">Auto-email each 1st</div>
          <Switch
            checked={!!data?.reports_email_monthly}
            onCheckedChange={toggleEmail}
            disabled={savingToggle || !data}
            data-testid="reports-email-toggle"
          />
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-16 text-zinc-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading report…
        </div>
      )}

      {!loading && data && (
        <div className="space-y-4">
          {/* Headline */}
          <SectionCard title="Headline" icon={Clock} onDownload={() => downloadPdf("headline")} loading={downloading === "headline"} testid="section-headline">
            <StatsGrid items={[
              { label: "Total hours", value: fmtNum(s.headline?.total_hours), icon: Clock },
              { label: "Payroll cost", value: fmtMoney(s.headline?.payroll_cost), icon: DollarSign },
              { label: "Jobs completed", value: fmtNum(s.headline?.jobs_completed), icon: Hammer },
              { label: "Jobs active", value: fmtNum(s.headline?.jobs_active), icon: Hammer },
              { label: "Revenue invoiced", value: fmtMoney(s.headline?.revenue_invoiced), icon: Receipt },
              { label: "Revenue paid", value: fmtMoney(s.headline?.revenue_paid), icon: Receipt },
            ]} />
          </SectionCard>

          {/* Crew */}
          <SectionCard title="Crew" icon={Users} onDownload={() => downloadPdf("crew")} loading={downloading === "crew"} testid="section-crew">
            <StatsGrid items={[
              { label: "Workers with hours", value: fmtNum(s.crew?.total_workers_with_hours) },
              { label: "Leave days taken", value: fmtNum(s.crew?.leave_days_taken) },
              { label: "Leave requests", value: fmtNum(s.crew?.leave_count) },
              { label: "Active users", value: fmtNum(s.crew?.active_users) },
            ]} />
            {(s.crew?.rows || []).length > 0 && (
              <div className="mt-4">
                <div className="label-tag mb-2">Top workers by hours</div>
                <Table headers={["Worker", "Hours"]}>
                  {s.crew.rows.slice(0, 10).map((r) => (
                    <tr key={r.user_id} className="border-b border-zinc-100 dark:border-zinc-900 last:border-0">
                      <td className="py-2 px-3 text-[13px]">{r.user_name}</td>
                      <td className="py-2 px-3 text-[13px] font-mono text-right">{r.hours.toLocaleString()}</td>
                    </tr>
                  ))}
                </Table>
              </div>
            )}
          </SectionCard>

          {/* Jobs */}
          <SectionCard title="Jobs" icon={Hammer} onDownload={() => downloadPdf("jobs")} loading={downloading === "jobs"} testid="section-jobs">
            <StatsGrid items={[
              { label: "Completed jobs", value: fmtNum(s.jobs?.completed_count) },
              { label: "Total value", value: fmtMoney(s.jobs?.completed_value) },
            ]} />
            {(s.jobs?.completed_jobs || []).length > 0 && (
              <div className="mt-4">
                <Table headers={["Job #", "Title", "Client", "Quoted"]}>
                  {s.jobs.completed_jobs.slice(0, 15).map((j) => (
                    <tr key={j.id} className="border-b border-zinc-100 dark:border-zinc-900 last:border-0">
                      <td className="py-2 px-3 text-[13px] font-mono">{j.job_number || "—"}</td>
                      <td className="py-2 px-3 text-[13px]">{j.title}</td>
                      <td className="py-2 px-3 text-[13px] text-zinc-500">{j.client_name || "—"}</td>
                      <td className="py-2 px-3 text-[13px] font-mono text-right">{fmtMoney(j.quoted_value)}</td>
                    </tr>
                  ))}
                </Table>
              </div>
            )}
          </SectionCard>

          {/* Back-costing */}
          <SectionCard title="Job back-costing" icon={Calculator} onDownload={() => downloadPdf("back_costing")} loading={downloading === "back_costing"} testid="section-back-costing" highlight>
            <StatsGrid items={[
              { label: "Quoted (total)", value: fmtMoney(s.back_costing?.totals?.quoted) },
              { label: "Actual labour", value: fmtMoney(s.back_costing?.totals?.actual) },
              { label: "Margin $", value: fmtMoney(s.back_costing?.totals?.margin), tone: (s.back_costing?.totals?.margin || 0) >= 0 ? "good" : "bad" },
              { label: "Margin %", value: s.back_costing?.totals?.margin_pct != null ? `${s.back_costing.totals.margin_pct.toFixed(1)}%` : "—", tone: (s.back_costing?.totals?.margin_pct || 0) >= 0 ? "good" : "bad" },
            ]} />
            {(s.back_costing?.rows || []).length > 0 && (
              <div className="mt-4">
                <Table headers={["Job #", "Title", "Quoted", "Actual", "Hours", "Margin", "%"]}>
                  {s.back_costing.rows.map((r) => {
                    const negative = (r.margin || 0) < 0;
                    return (
                      <tr key={r.id} className={`border-b border-zinc-100 dark:border-zinc-900 last:border-0 ${negative ? "bg-red-50/60 dark:bg-red-900/10" : ""}`}>
                        <td className="py-2 px-3 text-[13px] font-mono">{r.job_number || "—"}</td>
                        <td className="py-2 px-3 text-[13px]">{r.title}</td>
                        <td className="py-2 px-3 text-[13px] font-mono text-right">{fmtMoney(r.quoted)}</td>
                        <td className="py-2 px-3 text-[13px] font-mono text-right">{fmtMoney(r.actual)}</td>
                        <td className="py-2 px-3 text-[13px] font-mono text-right">{r.hours.toFixed(1)}</td>
                        <td className={`py-2 px-3 text-[13px] font-mono text-right font-bold ${negative ? "text-red-600" : "text-emerald-600"}`}>{fmtMoney(r.margin)}</td>
                        <td className={`py-2 px-3 text-[13px] font-mono text-right ${negative ? "text-red-600" : "text-emerald-600"}`}>{r.margin_pct != null ? `${r.margin_pct.toFixed(1)}%` : "—"}</td>
                      </tr>
                    );
                  })}
                </Table>
              </div>
            )}
          </SectionCard>

          {/* Job billing — invoiced vs cost */}
          <SectionCard title="Job billing — invoiced vs cost" icon={Receipt} onDownload={() => downloadPdf("job_billing")} loading={downloading === "job_billing"} testid="section-job-billing" highlight>
            <StatsGrid items={[
              { label: "Cost this month", value: fmtMoney(s.job_billing?.totals?.cost) },
              { label: "Invoiced this month", value: fmtMoney(s.job_billing?.totals?.invoiced) },
              { label: "Not yet invoiced", value: fmtMoney(s.job_billing?.totals?.uninvoiced), tone: (s.job_billing?.totals?.uninvoiced || 0) > 0 ? "bad" : "good" },
            ]} />
            {(s.job_billing?.rows || []).length > 0 ? (
              <div className="mt-4">
                <div className="label-tag mb-2">Per job · labour + machines + supplies vs invoices issued this month</div>
                <Table headers={["Job #", "Job", "Labour", "Machines", "Supplies", "Cost", "Invoiced", "Not invoiced"]}>
                  {s.job_billing.rows.map((r) => {
                    const gap = (r.uninvoiced || 0) > 0.005;
                    return (
                      <tr key={r.job_id} className={`border-b border-zinc-100 dark:border-zinc-900 last:border-0 ${gap ? "bg-amber-50/70 dark:bg-amber-900/10" : ""}`} data-testid={`job-billing-row-${r.job_id}`}>
                        <td className="py-2 px-3 text-[13px] font-mono">{r.job_number || "—"}</td>
                        <td className="py-2 px-3 text-[13px]">
                          {r.title}
                          {r.client_name && <span className="block text-[11px] text-zinc-500">{r.client_name}</span>}
                        </td>
                        <td className="py-2 px-3 text-[13px] font-mono text-right">
                          {fmtMoney(r.labour)}
                          {(r.hours || 0) > 0 && <span className="block text-[10px] text-zinc-500">{r.hours} hrs</span>}
                        </td>
                        <td className="py-2 px-3 text-[13px] font-mono text-right">
                          {fmtMoney(r.machines)}
                          {(r.machine_hours || 0) > 0 && <span className="block text-[10px] text-zinc-500">{r.machine_hours} hrs</span>}
                        </td>
                        <td className="py-2 px-3 text-[13px] font-mono text-right">{fmtMoney(r.supplies)}</td>
                        <td className="py-2 px-3 text-[13px] font-mono text-right font-bold">{fmtMoney(r.cost)}</td>
                        <td className="py-2 px-3 text-[13px] font-mono text-right">{fmtMoney(r.invoiced)}</td>
                        <td className={`py-2 px-3 text-[13px] font-mono text-right font-bold ${gap ? "text-amber-600 dark:text-amber-400" : "text-emerald-600"}`}>
                          {fmtMoney(r.uninvoiced)}
                        </td>
                      </tr>
                    );
                  })}
                </Table>
              </div>
            ) : (
              <div className="mt-3 text-sm text-zinc-500">No job activity recorded this month.</div>
            )}
          </SectionCard>

          {/* Safety */}
          <SectionCard title="Safety" icon={ShieldAlert} onDownload={() => downloadPdf("safety")} loading={downloading === "safety"} testid="section-safety">
            <StatsGrid items={[
              { label: "Hazards reported", value: fmtNum(s.safety?.hazards_reported) },
              { label: "Incidents", value: fmtNum(s.safety?.incidents_reported) },
              { label: "Toolbox talks", value: fmtNum(s.safety?.toolbox_talks_delivered) },
              { label: "Pre-starts", value: fmtNum(s.safety?.pre_starts_done) },
            ]} />
          </SectionCard>

          {/* QA */}
          <SectionCard title="Quality assurance" icon={ClipboardCheck} onDownload={() => downloadPdf("qa")} loading={downloading === "qa"} testid="section-qa">
            <StatsGrid items={[
              { label: "Records submitted", value: fmtNum(s.qa?.totals?.total) },
              { label: "Pass items", value: fmtNum(s.qa?.totals?.pass), tone: "good" },
              { label: "Fail items", value: fmtNum(s.qa?.totals?.fail), tone: (s.qa?.totals?.fail || 0) > 0 ? "bad" : null },
              { label: "N/A items", value: fmtNum(s.qa?.totals?.na) },
            ]} />
            {(s.qa?.by_task_type || []).length > 0 && (
              <div className="mt-4">
                <Table headers={["Task type", "Records", "Approved", "Rejected", "Pass", "Fail"]}>
                  {s.qa.by_task_type.map((r) => (
                    <tr key={r.task_type} className="border-b border-zinc-100 dark:border-zinc-900 last:border-0">
                      <td className="py-2 px-3 text-[13px]">{r.task_type}</td>
                      <td className="py-2 px-3 text-[13px] font-mono text-right">{r.count}</td>
                      <td className="py-2 px-3 text-[13px] font-mono text-right text-emerald-600">{r.approved}</td>
                      <td className="py-2 px-3 text-[13px] font-mono text-right text-red-600">{r.rejected}</td>
                      <td className="py-2 px-3 text-[13px] font-mono text-right">{r.pass}</td>
                      <td className="py-2 px-3 text-[13px] font-mono text-right">{r.fail}</td>
                    </tr>
                  ))}
                </Table>
              </div>
            )}
          </SectionCard>

          {/* Fleet */}
          <SectionCard title="Fleet" icon={Truck} onDownload={() => downloadPdf("fleet")} loading={downloading === "fleet"} testid="section-fleet">
            <StatsGrid items={[
              { label: "Vehicles", value: fmtNum(s.fleet?.vehicle_count) },
              { label: "WoF due (30d)", value: fmtNum(s.fleet?.wof_expiring), tone: (s.fleet?.wof_expiring || 0) > 0 ? "bad" : null },
              { label: "Rego due (30d)", value: fmtNum(s.fleet?.rego_expiring), tone: (s.fleet?.rego_expiring || 0) > 0 ? "bad" : null },
              { label: "Service due (30d)", value: fmtNum(s.fleet?.service_due) },
            ]} />
          </SectionCard>

          {/* Compliance */}
          <SectionCard title="Compliance" icon={Award} onDownload={() => downloadPdf("compliance")} loading={downloading === "compliance"} testid="section-compliance">
            <StatsGrid items={[
              { label: "Expiring (30d)", value: fmtNum(s.compliance?.expiring_30d?.length || 0), tone: (s.compliance?.expiring_30d?.length || 0) > 0 ? "bad" : null },
              { label: "Renewed this month", value: fmtNum(s.compliance?.renewed_this_month) },
            ]} />
            {(s.compliance?.expiring_30d || []).length > 0 && (
              <div className="mt-4">
                <Table headers={["Worker", "Competency", "Expires"]}>
                  {s.compliance.expiring_30d.map((c, i) => (
                    <tr key={i} className="border-b border-zinc-100 dark:border-zinc-900 last:border-0">
                      <td className="py-2 px-3 text-[13px]">{c.user_name || "—"}</td>
                      <td className="py-2 px-3 text-[13px]">{c.name}</td>
                      <td className="py-2 px-3 text-[13px] font-mono">{c.expiry_date}</td>
                    </tr>
                  ))}
                </Table>
              </div>
            )}
          </SectionCard>

          {/* Billing */}
          <SectionCard title="Billing & receivables" icon={Receipt} onDownload={() => downloadPdf("billing")} loading={downloading === "billing"} testid="section-billing">
            <StatsGrid items={[
              { label: "Invoices issued", value: fmtNum(s.billing?.issued_count) },
              { label: "Outstanding", value: fmtMoney(s.billing?.outstanding_total) },
              { label: "Overdue", value: fmtMoney(s.billing?.overdue_total), tone: (s.billing?.overdue_total || 0) > 0 ? "bad" : null },
            ]} />
            {(s.billing?.top_clients || []).length > 0 && (
              <div className="mt-4">
                <div className="label-tag mb-2">Top clients this month</div>
                <Table headers={["Client", "This month"]}>
                  {s.billing.top_clients.map((r, i) => (
                    <tr key={i} className="border-b border-zinc-100 dark:border-zinc-900 last:border-0">
                      <td className="py-2 px-3 text-[13px]">{r.client}</td>
                      <td className="py-2 px-3 text-[13px] font-mono text-right">{fmtMoney(r.value)}</td>
                    </tr>
                  ))}
                </Table>
              </div>
            )}
          </SectionCard>
        </div>
      )}

      <div className="mt-8 text-[11px] font-mono text-zinc-500 dark:text-zinc-400">
        Numbers exclude unapproved timesheets. Auto-email runs on the 1st of each month and sends the previous month&apos;s report to {data?.org_name || "your org"}&apos;s notification email.
      </div>
    </div>
  );
}

// ============================================================
// Reusable sub-components
// ============================================================

function SectionCard({ title, icon: Icon, children, onDownload, loading, testid, highlight }) {
  return (
    <div
      className={`hard-card p-4 lg:p-5 ${highlight ? "border-[#FF5F15] border-2" : ""}`}
      data-testid={testid}
    >
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-[#FF5F15]/10 text-[#FF5F15] rounded-sm flex items-center justify-center">
            <Icon className="w-4 h-4" />
          </div>
          <div className="font-display font-black uppercase tracking-tight text-base text-zinc-900 dark:text-white">{title}</div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={onDownload}
          disabled={loading}
          data-testid={`${testid}-pdf`}
          className="rounded-sm"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1.5" />}
          PDF
        </Button>
      </div>
      {children}
    </div>
  );
}

function StatsGrid({ items }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
      {items.map((it, i) => (
        <div
          key={i}
          className={`p-3 rounded-sm border ${
            it.tone === "good" ? "bg-emerald-50 dark:bg-emerald-900/15 border-emerald-200 dark:border-emerald-800"
            : it.tone === "bad" ? "bg-red-50 dark:bg-red-900/15 border-red-200 dark:border-red-800"
            : "bg-zinc-50 dark:bg-[#151C27] border-zinc-200 dark:border-zinc-800"
          }`}
        >
          <div className={`font-display font-black text-2xl leading-none ${
            it.tone === "good" ? "text-emerald-700 dark:text-emerald-300"
            : it.tone === "bad" ? "text-red-700 dark:text-red-300"
            : "text-zinc-900 dark:text-white"
          }`}>
            {it.value}
          </div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 dark:text-zinc-400 mt-1">
            {it.label}
          </div>
        </div>
      ))}
    </div>
  );
}

function Table({ headers, children }) {
  return (
    <div className="border border-zinc-200 dark:border-zinc-800 rounded-sm overflow-hidden overflow-x-auto">
      <table className="w-full">
        <thead className="bg-zinc-100 dark:bg-[#151C27] text-[10px] uppercase tracking-widest text-zinc-500 dark:text-zinc-400 font-bold">
          <tr>
            {headers.map((h, i) => (
              <th key={i} className={`py-2 px-3 ${i > 0 ? "text-right" : "text-left"}`}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}
